import React, { useState, useEffect } from 'react';
import {
Container,
Typography,
Button,
Paper,
TextField,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Box,
Chip,
Divider,
useTheme,
Grid,
Slide,
MenuItem,
InputLabel,
FormControl,
Select,
TableContainer,
Table,
TableHead,
TableRow,
TableCell,
TableBody,
Card,
CardContent,
Tabs,
Tab,
Alert,
LinearProgress,
CircularProgress,
Tooltip,
Menu
} from '@mui/material';
import {
PictureAsPdf,
TableChart,
GetApp,
FilterList,
Refresh,
Today,
Close,
Search,
Download,
TrendingUp,
TrendingDown,
Inventory,
AttachMoney,
Storage,
Warning,
CheckCircle,
Error,
CalendarMonth,
BarChart,
PieChart,
Summarize,
PointOfSale,
Receipt,
AccountBalance,
CompareArrows,
Description,
ShowChart,
Timeline
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import axios from 'axios';
import useCustomToast from '../../../hooks/useToast';
import {
APIBarDayClossingReport,
APIBarDayClossingReportDetails,
APIBarDayClossingReportStatistics,
APIBarDayClossingReportTrends
} from '../../../services/APIs';
import { TEXT_CONNECTION_ERROR } from '../../../services/Text';
import {formatNumberWithComma,
getCategoriesNameById,getUserDetails,formatCurrency} from '../../../utils/helpers';
import { exportToExcel,exportToPDF, 
    exportToCSV} from '../../../services/BarDayClossingReportService';
import { fetchBranchs } from '../../../utils/namesListUtils';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../../../utils/Permission';

const BarDayClosingReport = () => {
const theme = useTheme();
const [loading, setLoading] = useState(false);
const [trendsLoading, setTrendsLoading] = useState(false);
const [error, setError] = useState(null);
const showToast = useCustomToast();
const [exportAnchorEl, setExportAnchorEl] = useState(null);

const [reportData, setReportData] = useState({
    closings: [],
    summary: {
    total_closings: 0,
    total_sales: 0,
    total_expected_cash: 0,
    total_actual_cash: 0,
    total_variance: 0,
    by_status: {},
    by_branch: {},
    variance_analysis: {}
    },
    trends: [],
    filters_applied: {}
});

const [trendsData, setTrendsData] = useState([]);
const [statisticsData, setStatisticsData] = useState({});

const [filters, setFilters] = useState({
    branch_id: '',
    status: '',
    date_from: '',
    date_to: '',
    sort_by: 'clossing_date',
    sort_order: 'desc'
});

const [branchNames, setBranchNames] = useState([]);
const [activeTab, setActiveTab] = useState(0);
const [selectedClosing, setSelectedClosing] = useState(null);
const [detailDialogOpen, setDetailDialogOpen] = useState(false);
const [userRole, setUserRole] = useState('');
const [trendsPeriod, setTrendsPeriod] = useState('daily');
const [trendsRange, setTrendsRange] = useState(30);
const [detailsLoading, setDetailsLoading] = useState(false);

useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
    fetchBranchNames();
}, []);

useEffect(() => {
    if (activeTab === 0) {
    generateReport();
    }
    fetchStatistics();
}, [activeTab]);

useEffect(() => {
    if (activeTab === 1) {
    fetchTrends();
    }
}, [activeTab, trendsPeriod, trendsRange]);

const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

const fetchBranchNames = async () => {
    try {
    const data = await fetchBranchs();
    setBranchNames(data || []);
    } catch (error) {
    showToast("Error", error.message, "error");
    }
};


const generateReport = async () => {
    setLoading(true);
    setError(null);

    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {
        branch_id: branchIdToUse || '',
        status: filters.status || '',
        date_from: filters.date_from || '',
        date_to: filters.date_to || '',
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
    });

    const response = await axios.get(APIBarDayClossingReport, { params });

    if (response.data.status === 'success') {
        const data = response.data.data;
        setReportData({
        closings: data.closings || [],
        summary: data.summary || {
            total_closings: 0,
            total_sales: 0,
            total_expected_cash: 0,
            total_actual_cash: 0,
            total_variance: 0,
            by_status: {},
            by_branch: {},
            variance_analysis: {}
        },
        trends: data.trends || [],
        filters_applied: data.filters_applied || {}
        });
        showToast("Success", "Report generated successfully", "success");
    } else {
        throw new Error(response.data.message || "Failed to generate report");
    }
    } catch (error) {
    console.error('API Error:', error); // Debug log
    let errorMessage = "Failed to generate report";
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const fetchStatistics = async () => {
    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {
        branch_id: branchIdToUse || '',
        date_from: filters.date_from || '',
        date_to: filters.date_to || ''
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
    });

    const response = await axios.get(APIBarDayClossingReportStatistics, { params });

    if (response.data.status === 'success') {
        setStatisticsData(response.data.data || {});
    }
    } catch (error) {
    console.error('Failed to fetch statistics:', error);
    }
};

const fetchTrends = async () => {
    setTrendsLoading(true);
    try {
    let branchIdToUse = filters.branch_id;
    if (!hasPermission(PERMISSIONS.BRANCHES) && !branchIdToUse) {
        branchIdToUse = getUserDetails("branchId");
    }

    const params = {
        period: trendsPeriod,
        range: trendsRange,
        branch_id: branchIdToUse || '',
    };

    // Clean up empty params
    Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
    });

    const response = await axios.get(APIBarDayClossingReportTrends, { params });

    if (response.data.status === 'success') {
        setTrendsData(response.data.data.trends || []);
    } else {
        setTrendsData([]);
    }
    } catch (error) {
    console.error('Failed to fetch trends:', error);
    setTrendsData([]);
    } finally {
    setTrendsLoading(false);
    }
};

const fetchClosingDetails = async (closingId) => {
    try {
        const response = await axios.get(`${APIBarDayClossingReportDetails}/${closingId}`);
        
        if (response.data.status === 'success') {
            const data = response.data.data;
            
            // Handle different response structures
            if (data.closing) {
                // Structure 1: data.closing contains the closing object
                return {
                    ...data.closing,
                    financial_summary: data.financial_summary,
                    item_stats: data.item_stats
                };
            } else if (data.clossing_number) {
                // Structure 2: data is the closing object itself
                return data;
            } else {
                // Structure 3: data contains nested structure
                return {
                    clossing_number: data.clossing_number,
                    clossing_date: data.clossing_date,
                    branch_id: data.branch_id,
                    status: data.status || 'unknown',
                    opening_balance: data.opening_balance || 0,
                    sales_total: data.sales_total || 0,
                    clossing_balance: data.clossing_balance || 0,
                    expected_cash: data.expected_cash || 0,
                    actual_cash: data.actual_cash || 0,
                    variance: data.variance || 0,
                    items: data.items || [],
                    user: data.user || { name: 'N/A' },
                    notes: data.notes || '',
                    financial_summary: data.financial_summary,
                    item_stats: data.item_stats
                };
            }
        } else {
            throw new Error("Failed to fetch closing details");
        }
    } catch (error) {
        console.error('Failed to fetch closing details:', error);
        showToast("Error", "Failed to load closing details", "error");
        return null;
    }
};

const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
    ...prev,
    [name]: value
    }));
};

const handleDateRangeChange = (type, value) => {
    setFilters(prev => ({
    ...prev,
    [type]: value
    }));
};

const applyToday = () => {
    const today = new Date().toISOString().split('T')[0];
    setFilters({
    ...filters,
    date_from: today,
    date_to: today
    });
};

const applyThisWeek = () => {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    
    setFilters({
    ...filters,
    date_from: firstDay.toISOString().split('T')[0],
    date_to: lastDay.toISOString().split('T')[0]
    });
};

const applyThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    
    setFilters({
    ...filters,
    date_from: firstDay.toISOString().split('T')[0],
    date_to: lastDay.toISOString().split('T')[0]
    });
};

const clearFilters = () => {
    setFilters({
    branch_id: '',
    status: '',
    date_from: '',
    date_to: '',
    sort_by: 'clossing_date',
    sort_order: 'desc'
    });
};

const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
};

const handleExportClose = () => {
    setExportAnchorEl(null);
};

const handleExportExcel = async () => {
    try {
    handleExportClose();
    await exportToExcel(reportData, filters);
    showToast("Success", "Excel report downloaded successfully", "success");
    } catch (error) {
    console.error('Excel export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportPDF = async () => {
    try {
    handleExportClose();
    await exportToPDF(reportData, filters);
    showToast("Success", "PDF report downloaded successfully", "success");
    } catch (error) {
    console.error('PDF export error:', error);
    showToast("Error", error.message, "error");
    }
};

const handleExportCSV = async () => {
    try {
    handleExportClose();
    await exportToCSV(reportData, filters);
    showToast("Success", "CSV report downloaded successfully", "success");
    } catch (error) {
    console.error('CSV export error:', error);
    showToast("Error", error.message, "error");
    }
};

const getStatusColor = (status) => {
    if (!status) return theme.palette.grey[500];
    switch (status.toLowerCase()) {
    case 'approved': return theme.palette.success.main;
    case 'submitted': return theme.palette.info.main;
    case 'draft': return theme.palette.warning.main;
    case 'rejected': return theme.palette.error.main;
    default: return theme.palette.grey[500];
    }
};

const getVarianceColor = (variance) => {
    if (variance > 0) return theme.palette.success.main;
    if (variance < 0) return theme.palette.error.main;
    return theme.palette.info.main;
};

const handleViewDetails = async (closing) => {
    setDetailsLoading(true);
    try {
        const details = await fetchClosingDetails(closing.id);
        if (details) {
            // Ensure the closing object has all required properties
            const enrichedClosing = {
                ...closing, // Include original data as fallback
                ...details, // Override with API data
                clossing_number: details.clossing_number || closing.clossing_number,
                clossing_date: details.clossing_date || closing.clossing_date,
                branch_id: details.branch_id || closing.branch_id,
                status: details.closing?.status || details.status || closing.status || 'unknown',
                opening_balance: details.financial_summary?.opening_balance || details.opening_balance || closing.opening_balance || 0,
                sales_total: details.financial_summary?.sales_total || details.sales_total || closing.sales_total || 0,
                clossing_balance: details.financial_summary?.closing_balance || details.clossing_balance || closing.clossing_balance || 0,
                expected_cash: details.financial_summary?.expected_cash || details.expected_cash || closing.expected_cash || 0,
                actual_cash: details.financial_summary?.actual_cash || details.actual_cash || closing.actual_cash || 0,
                variance: details.financial_summary?.variance || details.variance || closing.variance || 0,
                items: details.closing?.items || details.items || closing.items || [],
                user: details.closing?.user || details.user || closing.user || { name: 'N/A' },
                notes: details.closing?.notes || details.notes || closing.notes || ''
            };
            setSelectedClosing(enrichedClosing);
        } else {
            // Fallback to basic closing data if API fails
            const fallbackClosing = {
                ...closing,
                status: closing.status || 'unknown',
                items: closing.items || [],
                user: closing.user || { name: 'N/A' }
            };
            setSelectedClosing(fallbackClosing);
        }
        setDetailDialogOpen(true);
    } catch (error) {
        console.error('Error in handleViewDetails:', error);
        // Fallback to basic closing data
        const fallbackClosing = {
            ...closing,
            status: closing.status || 'unknown',
            items: closing.items || [],
            user: closing.user || { name: 'N/A' }
        };
        setSelectedClosing(fallbackClosing);
        setDetailDialogOpen(true);

    }
    finally {
        setDetailsLoading(false);
    }

};

const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
};

const handleTrendsPeriodChange = (period) => {
    setTrendsPeriod(period);
};

const handleTrendsRangeChange = (range) => {
    setTrendsRange(range);
};

if (loading && !reportData.closings.length && activeTab === 0) {
    return (
    <Container maxWidth="xl" sx={{ py: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Box textAlign="center">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Generating Report...</Typography>
        </Box>
    </Container>
    );
}

return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
        <Typography variant="h6" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Bar Day Closing Report
        </Typography>
        <Typography variant="body2" color="text.secondary">
            Comprehensive daily closing analysis and cash management
        </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
            variant="contained"
            color="primary"
            startIcon={<Refresh />}
            onClick={generateReport}
            disabled={activeTab !== 0}
        >
            Refresh Report
        </Button>
        <Button
            variant="contained"
            color="success"
            startIcon={<GetApp />}
            onClick={handleExportClick}
            disabled={!reportData.closings || reportData.closings.length === 0}
        >
            Export Report
        </Button>
        </Box>
    </Box>

    {/* Filters Section */}
    <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FilterList /> Report Filters
        </Typography>
        
        <Grid container spacing={3}>
        {/* Quick Date Filters */}
        <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
                variant="outlined"
                startIcon={<Today />}
                onClick={applyToday}
            >
                Today
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisWeek}
            >
                This Week
            </Button>
            <Button
                variant="outlined"
                startIcon={<CalendarMonth />}
                onClick={applyThisMonth}
            >
                This Month
            </Button>
            <Button
                variant="outlined"
                color="error"
                startIcon={<Close />}
                onClick={clearFilters}
            >
                Clear All
            </Button>
            </Box>
        </Grid>

        {/* Branch Filter */}
        {hasPermission(PERMISSIONS.BRANCHES) && (
            <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
                <InputLabel>Branch</InputLabel>
                <Select
                name="branch_id"
                value={filters.branch_id}
                onChange={handleFilterChange}
                label="Branch"
                >
                <MenuItem value="">All Branches</MenuItem>
                {branchNames.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                    {branch.name} ({branch.code})
                    </MenuItem>
                ))}
                </Select>
            </FormControl>
            </Grid>
        )}


        {/* Status Filter */}
        <Grid item xs={12} md={6} lg={3}>
            <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Status"
            >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="submitted">Submitted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Date Range */}
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="From Date"
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={(e) => handleDateRangeChange('date_from', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
            <TextField
            fullWidth
            label="To Date"
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={(e) => handleDateRangeChange('date_to', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            />
        </Grid>

        {/* Sort Options */}
        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
                name="sort_by"
                value={filters.sort_by}
                onChange={handleFilterChange}
                label="Sort By"
            >
                <MenuItem value="clossing_date">Closing Date</MenuItem>
                <MenuItem value="sales_total">Sales Total</MenuItem>
                <MenuItem value="actual_cash">Actual Cash</MenuItem>
                <MenuItem value="variance">Variance</MenuItem>
                <MenuItem value="created_at">Created Date</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        <Grid item xs={12} md={6} lg={2}>
            <FormControl fullWidth size="small">
            <InputLabel>Sort Order</InputLabel>
            <Select
                name="sort_order"
                value={filters.sort_order}
                onChange={handleFilterChange}
                label="Sort Order"
            >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
            </Select>
            </FormControl>
        </Grid>

        {/* Generate Button */}
        <Grid item xs={12}>
            <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            onClick={generateReport}
            startIcon={<Search />}
            sx={{ mt: 2 }}
            >
            Generate Report
            </Button>
        </Grid>
        </Grid>

        {/* Active Filters */}
        {Object.keys(filters).some(key => filters[key] && !['sort_by', 'sort_order'].includes(key)) && (
        <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Active Filters:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {filters.branch_id && (
                <Chip
                label={`Branch: ${getCategoriesNameById(filters.branch_id, branchNames)}`}
                onDelete={() => handleFilterChange({ target: { name: 'branch_id', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.status && (
                <Chip
                label={`Status: ${filters.status.charAt(0).toUpperCase() + filters.status.slice(1)}`}
                onDelete={() => handleFilterChange({ target: { name: 'status', value: '' } })}
                color="primary"
                size="small"
                />
            )}
            {filters.date_from && filters.date_to && (
                <Chip
                label={`Date: ${filters.date_from} to ${filters.date_to}`}
                onDelete={() => {
                    handleFilterChange({ target: { name: 'date_from', value: '' } });
                    handleFilterChange({ target: { name: 'date_to', value: '' } });
                }}
                color="primary"
                size="small"
                />
            )}
            </Box>
        </Box>
        )}
    </Paper>

    {/* Summary Cards - Only show when we have data */}
    {reportData.summary && reportData.summary.total_closings > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Closings */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <Description sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatNumberWithComma(reportData.summary.total_closings || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Total Closings
                </Typography>
                {statisticsData.today && (
                <Typography variant="caption" color="primary">
                    Today: {statisticsData.today.count || 0}
                </Typography>
                )}
            </CardContent>
            </Card>
        </Grid>

        {/* Total Sales */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <PointOfSale sx={{ fontSize: 40, color: theme.palette.success.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.total_sales || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Total Sales
                </Typography>
                {statisticsData.today && (
                <Typography variant="caption" color="success">
                    Today: {formatCurrency(statisticsData.today.total_sales || 0)}
                </Typography>
                )}
            </CardContent>
            </Card>
        </Grid>

        {/* Total Expected Cash */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <AccountBalance sx={{ fontSize: 40, color: theme.palette.info.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.total_expected_cash || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Expected Cash
                </Typography>
            </CardContent>
            </Card>
        </Grid>

        {/* Total Actual Cash */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
                <Receipt sx={{ fontSize: 40, color: theme.palette.warning.main, mb: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(reportData.summary.total_actual_cash || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Actual Cash
                </Typography>
            </CardContent>
            </Card>
        </Grid>

        {/* Total Variance */}
        <Grid item xs={6} sm={4} md={3} lg={2}>
            <Card elevation={3} sx={{ 
            borderColor: reportData.summary.total_variance !== 0 ? 
                (reportData.summary.total_variance > 0 ? 'success.main' : 'error.main') : 'info.main' 
            }}>
            <CardContent sx={{ textAlign: 'center' }}>
                <CompareArrows sx={{ 
                fontSize: 40, 
                color: reportData.summary.total_variance !== 0 ? 
                    (reportData.summary.total_variance > 0 ? theme.palette.success.main : theme.palette.error.main) : 
                    theme.palette.info.main, 
                mb: 1 
                }} />
                <Typography variant="h6" sx={{ 
                fontWeight: 'bold',
                color: reportData.summary.total_variance !== 0 ? 
                    (reportData.summary.total_variance > 0 ? 'success.main' : 'error.main') : 'info.main'
                }}>
                {formatCurrency(reportData.summary.total_variance || 0)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Total Variance
                </Typography>
                {reportData.summary.variance_analysis && (
                <Typography variant="caption" 
                    sx={{ 
                    color: reportData.summary.variance_analysis.avg_variance > 0 ? 'success.main' : 'error.main'
                    }}
                >
                    Avg: {formatCurrency(reportData.summary.variance_analysis.avg_variance || 0)}
                </Typography>
                )}
            </CardContent>
            </Card>
        </Grid>
        </Grid>
    )}

    {/* Error Message */}
    {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
        {error}
        </Alert>
    )}

    {/* Report Tabs */}
    <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ bgcolor: theme.palette.primary.main }}
        >
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <Summarize /> Closing Records
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <ShowChart /> Trends Analysis
            </Box>
            }
        />
        <Tab 
            label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'white' }}>
                <PieChart /> Status Breakdown
            </Box>
            }
        />
        </Tabs>

        {/* Tab 1: Closing Records */}
        {activeTab === 0 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Day Closing Records
            </Typography>
            
            {reportData.closings && reportData.closings.length > 0 ? (
            <>
                <TableContainer>
                <Table>
                    <TableHead>
                    <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                        <TableCell><strong>#</strong></TableCell>
                        <TableCell><strong>Closing No.</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Sales Total</strong></TableCell>
                        <TableCell><strong>Expected Cash</strong></TableCell>
                        <TableCell><strong>Actual Cash</strong></TableCell>
                        <TableCell><strong>Variance</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                    </TableHead>
                    <TableBody>
                    {reportData.closings.map((closing, index) => (
                        <TableRow key={index} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ fontWeight: 'medium' }}>{closing.clossing_number}</TableCell>
                        <TableCell>
                            {new Date(closing.clossing_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                            })}
                        </TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {formatCurrency(closing.sales_total)}
                            </Typography>
                        </TableCell>
                        <TableCell>{formatCurrency(closing.expected_cash)}</TableCell>
                        <TableCell>
                            <Typography variant="body2" sx={{ 
                            fontWeight: 'bold',
                            color: theme.palette.warning.dark
                            }}>
                            {formatCurrency(closing.actual_cash)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Chip
                            label={formatCurrency(closing.variance)}
                            size="small"
                            sx={{
                                bgcolor: getVarianceColor(closing.variance),
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                            />
                        </TableCell>
                        <TableCell>
                            <Chip
                            label={closing.status.charAt(0).toUpperCase() + closing.status.slice(1)}
                            size="small"
                            sx={{
                                bgcolor: getStatusColor(closing.status),
                                color: 'white',
                                fontWeight: 'bold'
                            }}
                            />
                        </TableCell>
                        <TableCell>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleViewDetails(closing)}
                                disabled={!closing || !closing.id}
                            >
                                View Details
                            </Button>
                        </TableCell>
                        </TableRow>
                    ))}

                    {/* Footer Row */}
                    {reportData.closings.length > 0 && (
                        <TableRow sx={{ 
                        backgroundColor: 'black',
                        '& td': {
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1rem',
                            borderTop: `3px solid ${theme.palette.primary.main}`,
                            padding: '16px 8px'
                        }
                        }}>
                        <TableCell colSpan={4} align="center">
                            <Typography variant="h6" sx={{ color: 'white' }}>
                            GRAND TOTALS
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="h6" sx={{ 
                            color: 'white',
                            backgroundColor: theme.palette.success.dark,
                            padding: '8px 12px',
                            borderRadius: '4px',
                            display: 'inline-block'
                            }}>
                            {formatCurrency(reportData.summary.total_sales)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="h6" sx={{ color: 'white' }}>
                            {formatCurrency(reportData.summary.total_expected_cash)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="h6" sx={{ color: 'white' }}>
                            {formatCurrency(reportData.summary.total_actual_cash)}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography variant="h6" sx={{ 
                            color: 'white',
                            backgroundColor: reportData.summary.total_variance > 0 ? 
                                theme.palette.success.dark : theme.palette.error.dark,
                            padding: '8px 12px',
                            borderRadius: '4px',
                            display: 'inline-block'
                            }}>
                            {formatCurrency(reportData.summary.total_variance)}
                            </Typography>
                        </TableCell>
                        <TableCell colSpan={2}>
                            <Chip
                            label="SUMMARY"
                            sx={{
                                backgroundColor: theme.palette.primary.dark,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.875rem',
                                height: '32px'
                            }}
                            />
                        </TableCell>
                        </TableRow>
                    )}
                    </TableBody>
                </Table>
                </TableContainer>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    Showing {reportData.closings.length} closing records
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Generated on: {new Date().toLocaleString()}
                </Typography>
                </Box>
            </>
            ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <Description sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No closing records found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Try adjusting your filters or create new closings
                </Typography>
                <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={generateReport}
                >
                Refresh Data
                </Button>
            </Box>
            )}
        </Box>
        )}

        {/* Tab 2: Trends Analysis */}
        {activeTab === 1 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShowChart /> Trends Analysis
            </Typography>
            
            {/* Trends Filters */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                    <InputLabel>Period</InputLabel>
                    <Select
                    value={trendsPeriod}
                    onChange={(e) => handleTrendsPeriodChange(e.target.value)}
                    label="Period"
                    >
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                    <InputLabel>Range</InputLabel>
                    <Select
                    value={trendsRange}
                    onChange={(e) => handleTrendsRangeChange(e.target.value)}
                    label="Range"
                    >
                    <MenuItem value={7}>7 Days</MenuItem>
                    <MenuItem value={30}>30 Days</MenuItem>
                    <MenuItem value={90}>90 Days</MenuItem>
                    <MenuItem value={180}>6 Months</MenuItem>
                    <MenuItem value={365}>1 Year</MenuItem>
                    </Select>
                </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={fetchTrends}
                    startIcon={<Refresh />}
                    disabled={trendsLoading}
                >
                    {trendsLoading ? 'Loading...' : 'Update Trends'}
                </Button>
                </Grid>
            </Grid>
            </Paper>

            {trendsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
            ) : trendsData.length > 0 ? (
            <Grid container spacing={3}>
                {/* Sales Trends */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Timeline /> Sales Trends
                    </Typography>
                    <Box sx={{ height: 300, overflow: 'auto' }}>
                        {trendsData.map((trend, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                {trend.period}
                            </Typography>
                            <Typography variant="h6" color="primary">
                                {formatCurrency(trend.total_sales)}
                            </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Closings: {trend.closing_count}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Avg: {formatCurrency(trend.closing_count > 0 ? trend.total_sales / trend.closing_count : 0)}
                            </Typography>
                            </Box>
                            <LinearProgress 
                            variant="determinate" 
                            value={Math.min((trend.total_sales / Math.max(...trendsData.map(t => t.total_sales), 1)) * 100, 100)}
                            sx={{ mt: 1, height: 8, borderRadius: 4 }}
                            />
                        </Box>
                        ))}
                    </Box>
                    </CardContent>
                </Card>
                </Grid>

                {/* Variance Trends */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompareArrows /> Variance Trends
                    </Typography>
                    <Box sx={{ height: 300, overflow: 'auto' }}>
                        {trendsData.map((trend, index) => (
                        <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                                {trend.period}
                            </Typography>
                            <Chip
                                label={formatCurrency(trend.total_variance)}
                                color={trend.total_variance > 0 ? "success" : "error"}
                                size="small"
                            />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Avg Variance: {formatCurrency(trend.avg_variance)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Closings: {trend.closing_count}
                            </Typography>
                            </Box>
                            <LinearProgress 
                            variant="determinate" 
                            value={Math.min(Math.abs(trend.total_variance) / 100, 100)}
                            color={trend.total_variance > 0 ? "success" : "error"}
                            sx={{ mt: 1, height: 8, borderRadius: 4 }}
                            />
                        </Box>
                        ))}
                    </Box>
                    </CardContent>
                </Card>
                </Grid>
            </Grid>
            ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <ShowChart sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No trends data available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Try adjusting the filters or generate report first
                </Typography>
                <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => setActiveTab(0)}
                >
                Go to Closing Records
                </Button>
            </Box>
            )}
        </Box>
        )}

        {/* Tab 3: Status Breakdown */}
        {activeTab === 2 && (
        <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>
            Status Breakdown
            </Typography>
            
            {reportData.summary && reportData.summary.total_closings > 0 ? (
            <Grid container spacing={3}>
                {/* Status Distribution */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        Closing Status Distribution
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                        {Object.entries(reportData.summary.by_status || {}).map(([status, data], index) => {
                        const percentage = reportData.summary.total_closings > 0 ? 
                            (data.count / reportData.summary.total_closings) * 100 : 0;
                        
                        return (
                            <Box key={status} sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                                {status}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {data.count} ({percentage.toFixed(1)}%)
                                </Typography>
                            </Box>
                            <LinearProgress 
                                variant="determinate" 
                                value={percentage}
                                sx={{ 
                                height: 10, 
                                borderRadius: 5,
                                backgroundColor: theme.palette.grey[200],
                                '& .MuiLinearProgress-bar': {
                                    backgroundColor: getStatusColor(status),
                                    borderRadius: 5
                                }
                                }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                Sales: {formatCurrency(data.total_sales)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                Cash: {formatCurrency(data.total_actual_cash)}
                                </Typography>
                            </Box>
                            </Box>
                        );
                        })}
                    </Box>
                    </CardContent>
                </Card>
                </Grid>

                {/* Status Summary */}
                <Grid item xs={12} md={6}>
                <Card elevation={2}>
                    <CardContent>
                    <Typography variant="h6" sx={{ mb: 3 }}>
                        Status Summary
                    </Typography>
                    <Grid container spacing={2}>
                        {Object.entries(reportData.summary.by_status || {}).map(([status, data], index) => (
                        <Grid item xs={6} key={status}>
                            <Card sx={{ 
                            bgcolor: `${getStatusColor(status)}20`,
                            borderLeft: `4px solid ${getStatusColor(status)}`
                            }}>
                            <CardContent>
                                <Typography variant="subtitle1" sx={{ 
                                fontWeight: 'bold',
                                textTransform: 'capitalize',
                                color: getStatusColor(status)
                                }}>
                                {status}
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 1 }}>
                                {data.count}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                Sales: {formatCurrency(data.total_sales)}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                Cash: {formatCurrency(data.total_actual_cash)}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                Variance: {formatCurrency(data.total_variance)}
                                </Typography>
                            </CardContent>
                            </Card>
                        </Grid>
                        ))}
                    </Grid>
                    </CardContent>
                </Card>
                </Grid>

                {/* Variance Analysis */}
                {reportData.summary.variance_analysis && (
                <Grid item xs={12}>
                    <Card elevation={2}>
                    <CardContent>
                        <Typography variant="h6" sx={{ mb: 3 }}>
                        Variance Analysis
                        </Typography>
                        <Grid container spacing={2}>
                        <Grid item xs={6} md={3}>
                            <Card sx={{ bgcolor: 'success.light' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="success.dark">
                                {reportData.summary.variance_analysis.positive_variance_count || 0}
                                </Typography>
                                <Typography variant="body2">
                                Positive Variance
                                </Typography>
                            </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Card sx={{ bgcolor: 'error.light' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="error.dark">
                                {reportData.summary.variance_analysis.negative_variance_count || 0}
                                </Typography>
                                <Typography variant="body2">
                                Negative Variance
                                </Typography>
                            </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Card sx={{ bgcolor: 'info.light' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="info.dark">
                                {reportData.summary.variance_analysis.zero_variance_count || 0}
                                </Typography>
                                <Typography variant="body2">
                                Zero Variance
                                </Typography>
                            </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <Card sx={{ bgcolor: 'warning.light' }}>
                            <CardContent sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="warning.dark">
                                {formatCurrency(reportData.summary.variance_analysis.avg_variance || 0)}
                                </Typography>
                                <Typography variant="body2">
                                Average Variance
                                </Typography>
                            </CardContent>
                            </Card>
                        </Grid>
                        </Grid>
                    </CardContent>
                    </Card>
                </Grid>
                )}
            </Grid>
            ) : (
            <Box sx={{ textAlign: 'center', py: 6 }}>
                <PieChart sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                No status data available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                Generate a report first to see status breakdown
                </Typography>
                <Button 
                variant="contained" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => setActiveTab(0)}
                >
                Go to Closing Records
                </Button>
            </Box>
            )}
        </Box>
        )}
    </Paper>

     {/* Details Dialog */}
        <Dialog
            open={detailDialogOpen}
            onClose={() => setDetailDialogOpen(false)}
            maxWidth="lg"
            fullWidth
        >
            <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Closing Details - {selectedClosing?.clossing_number}</Typography>
                <IconButton onClick={() => setDetailDialogOpen(false)}>
                <Close />
                </IconButton>
            </Box>
            </DialogTitle>
            <DialogContent dividers>
                {detailsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                        <CircularProgress />
                        <Typography sx={{ ml: 2 }}>Loading details...</Typography>
                    </Box>
                ) :
                selectedClosing ? (
                    <Grid container spacing={3}>
                    {/* Basic Info */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Closing Information</Typography>
                            <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Closing Number:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {selectedClosing.clossing_number}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Closing Date:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {new Date(selectedClosing.clossing_date).toLocaleDateString()}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Branch:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {getCategoriesNameById(selectedClosing.branch_id, branchNames)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">User:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {selectedClosing.user?.name || 'N/A'}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Status:</Typography>
                                <Chip
                                    label={(selectedClosing.status || 'unknown').charAt(0).toUpperCase() + (selectedClosing.status || 'unknown').slice(1)}
                                    sx={{
                                        bgcolor: getStatusColor(selectedClosing.status),
                                        color: 'white',
                                        fontWeight: 'bold'
                                    }}
                                />
                            </Grid>
                            </Grid>
                        </CardContent>
                        </Card>
                    </Grid>
        
                    {/* Financial Summary */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Financial Summary</Typography>
                            <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Opening Balance:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {formatCurrency(selectedClosing.opening_balance)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Sales Total:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'success.main' }}>
                                {formatCurrency(selectedClosing.sales_total)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Closing Balance:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                {formatCurrency(selectedClosing.clossing_balance)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Expected Cash:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'info.main' }}>
                                {formatCurrency(selectedClosing.expected_cash)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Actual Cash:</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'medium', color: 'warning.main' }}>
                                {formatCurrency(selectedClosing.actual_cash)}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="body2" color="text.secondary">Variance:</Typography>
                                <Typography variant="body1" sx={{ 
                                fontWeight: 'bold',
                                color: getVarianceColor(selectedClosing.variance)
                                }}>
                                {formatCurrency(selectedClosing.variance)}
                                </Typography>
                            </Grid>
                            </Grid>
                        </CardContent>
                        </Card>
                    </Grid>
        
                    {/* Items Table */}
                    {selectedClosing.items && selectedClosing.items.length > 0 && (
                        <Grid item xs={12}>
                        <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                            <CardContent>
                            <Typography variant="h6" sx={{ mb: 2 }}>Items Details</Typography>
                            <TableContainer>
                                <Table size="small">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                        backgroundColor: theme.palette.primary.main,
                                        '& th': {
                                            color: 'white',
                                            fontWeight: 'bold',
                                            fontSize: '0.95rem'
                                        }
                                    }}>
                                    <TableCell>Item Name</TableCell>
                                    <TableCell align="right">Opening Qty</TableCell>
                                    <TableCell align="right">Sold Qty</TableCell>
                                    <TableCell align="right">Closing Qty</TableCell>
                                    <TableCell align="right">Opening Value</TableCell>
                                    <TableCell align="right">Sales Value</TableCell>
                                    <TableCell align="right">Closing Value</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {selectedClosing.items.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{item.stock?.name || 'N/A'}</TableCell>
                                        <TableCell align="right">{formatNumberWithComma(item.opening_quantity)}</TableCell>
                                        <TableCell align="right">{formatNumberWithComma(item.sold_quantity)}</TableCell>
                                        <TableCell align="right">{formatNumberWithComma(item.clossing_quantity)}</TableCell>
                                        <TableCell align="right">{formatCurrency(item.opening_value)}</TableCell>
                                        <TableCell align="right">{formatCurrency(item.sales_value)}</TableCell>
                                        <TableCell align="right">{formatCurrency(item.clossing_value)}</TableCell>
                                    </TableRow>
                                    ))}
                                </TableBody>
                                </Table>
                            </TableContainer>
                            </CardContent>
                        </Card>
                        </Grid>
                    )}
        
                    {/* Notes */}
                    {selectedClosing.notes && (
                        <Grid item xs={12}>
                        <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
                            <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Notes</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedClosing.notes}
                            </Typography>
                            </CardContent>
                        </Card>
                        </Grid>
                    )}
                    </Grid>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No details available</Typography>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
            <Button onClick={() => setDetailDialogOpen(false)} color="primary">
                Close
            </Button>
            </DialogActions>
        </Dialog>
    
        {/* Export Menu */}
        <Menu
            anchorEl={exportAnchorEl}
            open={Boolean(exportAnchorEl)}
            onClose={handleExportClose}
        >
            <MenuItem onClick={handleExportExcel} sx={{ gap: 1 }}>
            <TableChart fontSize="small" color="success" />
            Export to Excel (.xlsx)
            </MenuItem>
            <MenuItem onClick={handleExportPDF} sx={{ gap: 1 }}>
            <PictureAsPdf fontSize="small" color="error" />
            Export to PDF (.pdf)
            </MenuItem>
            <MenuItem onClick={handleExportCSV} sx={{ gap: 1 }}>
            <TableChart fontSize="small" color="info" />
            Export to CSV (.csv)
            </MenuItem>
        </Menu>
    </Container>
);
};

export default BarDayClosingReport;


